import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  avatarSizes,
  type AvatarSize,
} from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

/**
 * The agent's face, in its two costumes.
 *
 * - **In the back-office** an agent is a photo the team picked (`avatarId`, with an
 *   optional looping video beside it).
 * - **In the chat** a visitor meets the *tenant*, so the face is the tenant's logo,
 *   and failing that the agent's initial on the brand colour. Passing `logo` — even
 *   `null` — is what asks for that version: the photo catalogue means nothing to a
 *   visitor, and `f01` smiling at them from a hairdresser's site would be a stranger.
 *
 * One component for both, so the chat, its launcher preview and the onboarding (which
 * shows the photo the client is choosing, inside the chat's own bubbles) agree on size,
 * cropping and the "online" dot.
 */
type AgentAvatarProps = {
  name?: string;
  size?: AvatarSize;
  className?: string;
  /** A small "online" dot in the bottom-right corner, outside the clipped circle. */
  online?: boolean;
} & (
  | { avatarId?: string; logo?: never }
  | { logo: string | null | undefined; avatarId?: never }
);

export function AgentAvatar(props: AgentAvatarProps) {
  const { name, size, className, online } = props;
  const brand = "logo" in props;

  return (
    <div
      className={cn(
        "relative shrink-0",
        // The chat sizes its avatars by class and scales the initial off that (0.9em),
        // so it only takes the preset scale when it asks for one.
        size ? avatarSizes[size] : !brand && avatarSizes["2xl"],
        className,
      )}
    >
      {brand ? (
        <BrandFace name={name} logo={props.logo} />
      ) : (
        <PhotoFace avatarId={props.avatarId} name={name} />
      )}
      {online && (
        <span className="absolute -bottom-0.5 -right-0.5 block size-3 rounded-full bg-success ring-2 ring-background" />
      )}
    </div>
  );
}

function BrandFace({ name, logo }: { name?: string; logo?: string | null }) {
  const initial = (name?.trim()?.[0] || "?").toUpperCase();
  return (
    <Avatar className="size-full">
      {logo && (
        <AvatarImage src={logo} alt={name || ""} className="object-cover" />
      )}
      {/* Also what a logo that fails to load falls back to. The inline colour beats
          the hashed one AvatarFallback paints by default: this is the tenant's colour,
          the one `brandingStyle` sets, not a colour derived from a letter. */}
      <AvatarFallback
        className="select-none font-semibold text-primary-foreground"
        style={{ backgroundColor: "var(--color-primary)" }}
      >
        <span className="text-[0.9em] leading-none">{initial}</span>
      </AvatarFallback>
    </Avatar>
  );
}

function PhotoFace({
  avatarId = "f01",
  name = "Agent",
}: {
  avatarId?: string;
  name?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const avatarImage = `/avatars/${avatarId}.jpg`;
  const avatarVideoWebm = `/avatars/${avatarId}.webm`;
  const avatarVideoMp4 = `/avatars/${avatarId}.mp4`;

  const initials = (name || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Check if video exists (try webm first)
  useEffect(() => {
    setHasVideo(false);

    const video = document.createElement("video");
    video.innerHTML = `<source src="${avatarVideoWebm}" type="video/webm"><source src="${avatarVideoMp4}" type="video/mp4">`;

    const checkLoad = () => {
      if (video.readyState >= 2) {
        // HAVE_CURRENT_DATA or better
        setHasVideo(true);
      }
    };

    video.addEventListener("loadeddata", checkLoad);
    video.load();

    video.onerror = () => {
      setHasVideo(false);
    };

    return () => {
      video.removeEventListener("loadeddata", checkLoad);
    };
  }, [avatarVideoWebm, avatarVideoMp4]);

  // Implement ping-pong loop logic
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideo || !imageLoaded) return;

    // Start playing
    video.play().catch(() => {
      // Autoplay might be blocked, that's ok
    });
  }, [hasVideo, imageLoaded]);

  return (
    <Avatar className="size-full">
      {hasVideo && imageLoaded ? (
        <video
          ref={videoRef}
          muted
          playsInline
          loop
          className="h-full w-full object-cover rounded-full"
        >
          <source src={avatarVideoWebm} type="video/webm" />
          <source src={avatarVideoMp4} type="video/mp4" />
        </video>
      ) : (
        <AvatarImage
          src={avatarImage}
          alt={name}
          onLoad={() => setImageLoaded(true)}
        />
      )}
      <AvatarFallback className="bg-slate-500/5 text-primary font-semibold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
